import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import SmartImage from 'dpl/components/SmartImage';
import Icon from 'dpl/common/components/Icon';
import { isLessThanBreakpoint } from 'dpl/util/grid';

import trustedNetworkPhoto from 'dpl/assets/homepage/gd_home_trusted_network.png';
import communicationPhoto from 'dpl/assets/homepage/gd_home_communication.png';
import ownershipPhoto from 'dpl/assets/homepage/gd_home_ownership.png';

const DRAWER_ITEMS = [
  {
    title: '01. Search our vetted network',
    photo_url: trustedNetworkPhoto,
    description:
      'Finding the right dog starts with knowing who to trust. That’s why we’ve reviewed more than 175,000 programs nationwide, accepting only 1 in 5 into the Good Dog network. Search confidently in one place and filter by what matters most.'
  },
  {
    title: '02. Connect directly on Good Dog',
    photo_url: communicationPhoto,
    description:
      'Ask questions, get to know each other, and share updates directly on Good Dog as you work toward pickup day. Work together to find the right match, without having to figure it all out on your own.'
  },
  {
    title: '03. Pay with protection',
    photo_url: ownershipPhoto,
    description:
      'Pay through Good Dog with Payment Protection, so you can move forward with confidence knowing your payment is secure and help is there if you need it.'
  }
];

function Drawer({ drawerItem, handleDropdownClick, idx, selectedDropdowns }) {
  const isOpen = selectedDropdowns.includes(idx);

  return (
    <div className="GoodDogHomeSection__drawerItem">
      <div
        className={classnames('bb b--gray-300', {
          bt: idx === 0
        })}
      >
        <button
          type="button"
          className={classnames('flex items-center w-100 pt6 pb6', {
            'pb4-md': isOpen
          })}
          onClick={() => handleDropdownClick(idx)}
        >
          <div className="flex-auto">
            <h5
              className={classnames(
                'flex items-center font-18 lh-normal transition',
                {
                  'o-80': !isOpen
                }
              )}
            >
              {drawerItem.title}
            </h5>
          </div>
          <Icon
            name="fetch-chevron-down"
            className={classnames('tertiary arrow-icon', {
              'arrow-icon__open': isOpen
            })}
            height="24px"
            width="24px"
          />
        </button>
        <div
          className={classnames(
            'GoodDogHomeSection__drawerBody overflow-hidden',
            {
              open: isOpen
            }
          )}
        >
          {isOpen && (
            <>
              <div className="GoodDogHomeSection__drawerImage md:hidden lg:hidden">
                <SmartImage
                  lazy
                  src={DRAWER_ITEMS[idx].photo_url}
                  className="br4"
                />
              </div>
              <p className="secondary o-80 w-100 pt6 pt0-md pb6">
                {drawerItem.description}
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

Drawer.propTypes = {
  drawerItem: PropTypes.shape({
    description: PropTypes.string,
    title: PropTypes.string
  }).isRequired,
  handleDropdownClick: PropTypes.func.isRequired,
  idx: PropTypes.number.isRequired,
  selectedDropdowns: PropTypes.arrayOf(PropTypes.number).isRequired
};

const IS_LT_MD = isLessThanBreakpoint('md');

export default function GoodDogHomeSection() {
  const [selectedDropdowns, setSelectedDropdowns] = useState([0]);
  const [fadeInClass, setFadeInClass] = useState('');

  function handleDropdownClick(idx) {
    if (IS_LT_MD && !selectedDropdowns.includes(idx)) {
      // If the drawer is not open, open it
      setSelectedDropdowns([...selectedDropdowns, idx]);
    } else if (IS_LT_MD && selectedDropdowns.includes(idx)) {
      // If the drawer is open, close it
      setSelectedDropdowns([...selectedDropdowns.filter(i => i !== idx)]);
    } else if (selectedDropdowns[0] === idx && idx < DRAWER_ITEMS.length - 1) {
      // Clicking on the open drawer will close it and open the next one
      setSelectedDropdowns([idx + 1]);
    } else if (
      selectedDropdowns[0] === idx &&
      idx === DRAWER_ITEMS.length - 1
    ) {
      // Clicking on the last drawer when it is already open will open the first drawer again
      setSelectedDropdowns([0]);
    } else {
      setSelectedDropdowns([idx]);
    }

    setFadeInClass('fade-in');
  }

  return (
    <section className="GoodDogHomeSection section-vertical-padding">
      <div className="container-xl section-fade">
        <div className="tc-ns">
          <div className="flex justify-center">
            <h3 className="font-28 md:font-40 w-80-md w-60-lg mb4 lh-title">
              Finding your dog should feel good from the start
            </h3>
          </div>
          <p className="secondary o-80 font-16 md:font-18 mb6 mb18-md">
            Here’s how it works on Good Dog.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-32">
          <div>
            {DRAWER_ITEMS.map((drawerItem, idx) => (
              <Drawer
                key={idx}
                drawerItem={drawerItem}
                handleDropdownClick={handleDropdownClick}
                idx={idx}
                selectedDropdowns={selectedDropdowns}
              />
            ))}
          </div>
          <div className="hidden md:block lg:block">
            <div className="flex justify-end">
              <div
                className={classnames(
                  'GoodDogHomeSection__drawerImage w-100',
                  fadeInClass
                )}
                onAnimationEnd={() => setFadeInClass('')}
              >
                <div className="aspect-ratio aspect-ratio--1x1 z-0">
                  <div className="aspect-ratio--object">
                    <div className="h-100 w-100 overflow-hidden f0">
                      <SmartImage
                        Tag="div"
                        lazy
                        src={DRAWER_ITEMS[selectedDropdowns[0]]?.photo_url}
                        className="cover bg-center h-100 w-100"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
